import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { SchemaBlock } from "src/services/schema/types/schema.types";

interface SchemaContextType {
  schemas: SchemaBlock[];
  setSchemas: (schemas: SchemaBlock[]) => void;
  addSchemas: (schemas: SchemaBlock[], id?: string) => void;
  removeSchemas: (id: string) => void;
  clearSchemas: () => void;
}

const SchemaContext = createContext<SchemaContextType | undefined>(undefined);

interface SchemaProviderProps {
  children: ReactNode;
}

/**
 * Schema provider component that manages global schema state
 */
export const SchemaProvider: React.FC<SchemaProviderProps> = ({ children }) => {
  const [schemas, setSchemas] = useState<SchemaBlock[]>([]);
  const [schemaMap, setSchemaMap] = useState<Map<string, SchemaBlock[]>>(
    new Map()
  );

  const clearSchemas = useCallback(() => {
    setSchemas([]);
    setSchemaMap(new Map());
  }, []);

  const addSchemas = useCallback((newSchemas: SchemaBlock[], id?: string) => {
    if (id) {
      setSchemaMap((prevMap) => {
        const newMap = new Map(prevMap);
        newMap.set(id, newSchemas);
        return newMap;
      });
    } else {
      setSchemas((prevSchemas) => [...prevSchemas, ...newSchemas]);
    }
  }, []);

  const removeSchemas = useCallback((id: string) => {
    setSchemaMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  // Combine schemas from both sources
  const allSchemas = useMemo(() => {
    const mapSchemas = Array.from(schemaMap.values()).flat();
    return [...schemas, ...mapSchemas];
  }, [schemas, schemaMap]);

  const value: SchemaContextType = useMemo(
    () => ({
      schemas: allSchemas,
      setSchemas,
      addSchemas,
      removeSchemas,
      clearSchemas,
    }),
    [allSchemas, addSchemas, removeSchemas, clearSchemas]
  );

  return (
    <SchemaContext.Provider value={value}>{children}</SchemaContext.Provider>
  );
};

/**
 * Hook to access schema context
 * @returns Schema context with schemas, setSchemas, addSchemas, removeSchemas, and clearSchemas
 * @throws Error if used outside of SchemaProvider
 */
export const useSchema = (): SchemaContextType => {
  const context = useContext(SchemaContext);

  if (context === undefined) {
    throw new Error("useSchema must be used within a SchemaProvider");
  }

  return context;
};
